import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ArrowLeft, Play, HelpCircle, Users, Award } from "lucide-react";
import { Reveal } from "../components/common";
import { getIcon } from "../lib/icons";
import { publicApi, mediaUrl, mediaSrcSet } from "../lib/api";
import { videoEmbedUrl } from "../lib/utils";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import { XeroCostAnswer } from "../components/XeroCostAnswer";
import AskCassieRelated, { subjectForService } from "../components/AskCassieRelated";
import Seo, { SITE_NAME, breadcrumbSchema } from "../components/Seo";
import BookCallButton from "../components/BookCallButton";

const CTA_HEADING = "Let's Talk About Your Business";
const CTA_BODY =
  "Every business is different, so your price is built around your business and the services you need rather than put into a package.\n\nWhether you're looking for bookkeeping, payroll, tax support, Xero expertise or advice to help your business grow, we'd love to learn more about you.\n\nYou can build an indicative price online in a few minutes, and we'll verify the detail and confirm it on a short Discovery Call.\n\nNo pressure. No obligation. Just honest advice from people who genuinely want to help your business succeed.";

// Some service records store their body with a leading markdown heading
// ("### Why Choose CASS for ..."). This block is rendered as plain text under a
// static heading, so the hashes leaked into the visible — and indexable — copy.
const stripLeadingHeading = (text = "") => text.replace(/^\s*#{1,6}\s+.*(\r?\n)+/, "").trim();


const Section = ({ s }) => (
  <section id={s.anchor} className="py-12 md:py-16 bg-white border-t border-slate-100">
    <div className="max-w-5xl mx-auto px-6">
      <Reveal>
        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">{s.heading}</h2>
        {(s.body || []).map((p, i) => (
          <p key={i} className="text-slate-600 text-base sm:text-lg leading-relaxed mb-4">{p}</p>
        ))}
        {(s.bullets || []).length > 0 && (
          <ul className="space-y-2.5 mt-2">
            {s.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="text-orange shrink-0 mt-1" size={18} />
                <span className="text-[15px] text-navy leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {(s.steps || []).length > 0 && (
          <ol className="space-y-5 mt-2">
            {s.steps.map(([title, text], i) => (
              <li key={title} className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-orange text-white font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                <span>
                  <strong className="block text-navy">{title}</strong>
                  <span className="text-slate-600 text-[15px] leading-relaxed">{text}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
        {s.footer && <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-5">{s.footer}</p>}
        {s.link && (
          <Link to={s.link.to} className="inline-flex items-center gap-2 mt-5 text-orange font-bold text-sm hover:gap-3 transition-all">
            {s.link.label} <ArrowRight size={16} />
          </Link>
        )}
      </Reveal>
    </div>
  </section>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [all, setAll] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [packageLive, setPackageLive] = useState(false);
  const [xeroCost, setXeroCost] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPlaying(false);
    publicApi.service(slug).then(setService).catch(() => setService(null)).finally(() => setLoading(false));
    publicApi.services().then(setAll).catch(() => {});
    publicApi.caseStudies().then(setStories).catch(() => {});
    publicApi.packagePage().then((c) => setPackageLive((c.visibility || "live") === "live")).catch(() => {});
    publicApi.xeroPricing().then(setXeroCost).catch(() => {});
  }, [slug]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-400">Loading...</div>;
  }

  if (!service) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Seo title="Service not found" noindex />
        <h1 className="font-display font-extrabold text-3xl text-navy mb-4">Service not found</h1>
        <Link to="/services" className="text-orange font-bold inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to services</Link>
      </div>
    );
  }

  // A consolidated service keeps its place in the menu but lives at another URL.
  if (service.external_path) {
    return <Navigate to={service.external_path} replace />;
  }


  const Icon = getIcon(service.icon);
  const related = (
    service.related && service.related.length
      ? service.related.map((rs) => all.find((s) => s.slug === rs)).filter(Boolean)
      : all.filter((s) => s.slug !== slug)
  ).slice(0, 3);
  const heroEmbed = videoEmbedUrl(service.hero_video || "");
  const uploadedHero = service.hero_video_upload || "";
  const hasMedia = uploadedHero || heroEmbed || service.hero_image;
  const imageOnly = !uploadedHero && !heroEmbed && !!service.hero_image;
  const ytId = (service.hero_video || "").match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/)?.[1];
  const poster = service.hero_image ? mediaUrl(service.hero_image) : (uploadedHero ? mediaUrl(`${uploadedHero}/poster`) : (ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : ""));
  const playUrl = heroEmbed.includes("youtube.com") ? `${heroEmbed}&autoplay=1` : `${heroEmbed}?autoplay=1`;

  const points = service.points || [];
  const whoFor = service.who_for || [];
  const sections = service.sections || [];
  const slot = (name) => sections.filter((s) => s.slot === name);
  const serviceFaqs = (service.faqs || []).filter((f) => f && f.q);
  // The Xero cost answer owns this page's metadata and leads its FAQs, because the
  // search traffic it targets is asking what Xero costs before anything else.
  const costAnswer = xeroCost && xeroCost.enabled && xeroCost.service_slug === slug ? xeroCost : null;
  const costFaqs = costAnswer ? (costAnswer.faqs || []).filter((f) => f && f.q) : [];
  const SHARED_SERVICE_FAQS = [
    { q: "How much do your accounting services cost?", a: "Pricing is tailored around your business and the services you need, so there is no standard price list. You can build an indicative price online in a few minutes on our Pricing page — pick the services you need, answer a few questions and see a figure straight away. It reflects the information you give us rather than being a guaranteed quotation: we verify the detail and confirm the price on a short Discovery Call and in your proposal. Some businesses and service combinations need that conversation before we can give a figure at all." },
    { q: "Do you offer fixed monthly fees?", a: "Yes. Most of our clients prefer a fixed monthly fee spread evenly across the year, so there are no surprise bills and you can budget with confidence. We agree the scope together and only revisit it if your needs genuinely change." },
    { q: "Are you Xero specialists?", a: "Yes — we're a Xero Platinum Partner and work in Xero every day. If you're not on Xero yet, we can handle the full migration and training for you so the switch is completely painless." },
    { q: "Do I have to change accountants mid-year?", a: "Not at all. You can switch to CASS at any point in your financial year. We'll take care of the professional handover with your previous accountant and make the transition smooth and stress-free." },
  ];
  const pageFaqs = [...costFaqs, ...serviceFaqs.filter((f) => !costFaqs.some((cf) => cf.q.toLowerCase() === f.q.toLowerCase()))];
  const faqs = [...pageFaqs, ...SHARED_SERVICE_FAQS.filter((sf) => !pageFaqs.some((f) => f.q.toLowerCase() === sf.q.toLowerCase()))];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.short || service.long || "",
    serviceType: service.title,
    provider: { "@type": "AccountingService", name: SITE_NAME },
    areaServed: "United Kingdom",
    ...(points.length
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${service.title} — what's included`,
            itemListElement: points.map((p) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: p } })),
          },
        }
      : {}),
  };
  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const breadcrumb = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.title, path: `/services/${slug}` },
  ]);

  return (
    <div>
      <Seo
        title={costAnswer?.seo_title || service.meta_title || service.title}
        description={costAnswer?.seo_description || service.meta_description || service.short || service.long}
        image={service.hero_image ? mediaUrl(service.hero_image) : ""}
        jsonLd={[serviceSchema, breadcrumb, ...(faqSchema ? [faqSchema] : [])]}
      />

      {/* 1. Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Link to="/services" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange text-sm font-semibold mb-6 transition-colors"><ArrowLeft size={16} /> All services</Link>
          <div className={`grid gap-10 items-center ${hasMedia ? "lg:grid-cols-2" : ""}`}>
            <div>
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-orange flex items-center justify-center shrink-0"><Icon size={30} className="text-white" /></div>
                <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95]">{service.h1 || service.title}</h1>
              </div>
              <p className="mt-5 text-orange font-semibold text-lg max-w-2xl leading-relaxed">{service.short}</p>
              {service.long && <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed whitespace-pre-line">{service.long}</p>}
              <div className="mt-8 flex flex-wrap gap-3">
                {service.hero_cta ? (
                  <>
                    <a href={service.hero_cta.primary.to} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">{service.hero_cta.primary.label} <ArrowRight size={16} /></a>
                    <Link to={service.hero_cta.secondary.to} data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all">{service.hero_cta.secondary.label}</Link>
                  </>
                ) : (
                  <>
                    <Link to={`/contact?service=${encodeURIComponent(service.title)}`} data-testid="hero-lets-talk" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={16} /></Link>
                    <Link to="/services" data-testid="hero-explore-services" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all">Explore Our Services</Link>
                  </>
                )}
              </div>
              {/* Anyone reading a Xero page is likely to want the software's price too. */}
              {service.group === "xero-software" && !costAnswer && xeroCost?.enabled && (
                <p className="mt-5 text-sm text-slate-300">
                  <Link to={`/services/${xeroCost.service_slug}`} data-testid="link-xero-cost" className="text-orange font-semibold underline underline-offset-4 decoration-orange/40 hover:decoration-orange transition-colors">
                    How much is Xero per month?
                  </Link>{" "}
                  See every UK plan and price.
                </p>
              )}
            </div>
            {hasMedia && (
              <div data-testid="service-hero-media" className={`relative rounded-2xl overflow-hidden aspect-video w-full lg:max-w-[70%] lg:ml-auto ${imageOnly ? "" : "shadow-2xl bg-navy-deep"}`}>
                {uploadedHero ? (
                  playing ? (
                    <video data-testid="service-hero-video-uploaded" className="w-full h-full bg-black" src={mediaUrl(uploadedHero)} poster={poster || undefined} autoPlay controls playsInline preload="auto" />
                  ) : (
                    <button type="button" data-testid="service-hero-play" onClick={() => setPlaying(true)} aria-label="Play video" className="group w-full h-full relative">
                      {poster ? <img loading="lazy" decoding="async" src={poster} alt={service.title} className={`w-full h-full ${service.hero_image ? "object-contain" : "object-cover"}`} onError={(e) => { e.target.style.display = "none"; }} /> : <div className="w-full h-full" />}
                      <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                        <span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span>
                      </span>
                    </button>
                  )
                ) : heroEmbed ? (
                  playing ? (
                    <iframe data-testid="service-hero-video" className="w-full h-full" src={playUrl} title={service.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <button type="button" data-testid="service-hero-play" onClick={() => setPlaying(true)} aria-label="Play video" className="group w-full h-full relative">
                      {poster ? (
                        <img loading="lazy" decoding="async" src={poster} alt={service.title} className="w-full h-full object-cover" onError={(e) => { if (ytId && !e.target.dataset.fb) { e.target.dataset.fb = "1"; e.target.src = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`; } }} />
                      ) : <div className="w-full h-full" />}
                      <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                        <span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span>
                      </span>
                    </button>
                  )
                ) : (
                  <img loading="lazy" decoding="async" data-testid="service-hero-image" src={mediaUrl(service.hero_image, 1080)} srcSet={mediaSrcSet(service.hero_image)} sizes="(max-width: 767px) 100vw, 560px" alt={service.title} className="w-full h-full object-contain"
                    onError={(e) => { const box = e.target.closest("[data-testid='service-hero-media']"); if (box) box.style.display = "none"; }} />
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. The cost answer, above the service content — cost searchers won't scroll for it */}
      {costAnswer && <div id="xero-prices"><XeroCostAnswer cfg={costAnswer} /></div>}

      {slot("intro").map((s) => <Section key={s.heading} s={s} />)}

      {/* 3. What's Included */}
      {points.length > 0 && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange"><CheckCircle2 size={22} /></span>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">What's Included</h2>
              </div>
              {service.points_intro && (
                <p className="text-slate-600 text-base sm:text-lg mb-6">{service.points_intro}</p>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                {points.map((p) => (
                  <div key={p} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <CheckCircle2 className="text-orange shrink-0 mt-0.5" size={20} />
                    <span className="text-[15px] font-medium text-navy">{p}</span>
                  </div>
                ))}
              </div>
              {service.points_footer && (
                <p className="text-slate-600 text-base sm:text-lg mt-6 leading-relaxed">{service.points_footer}</p>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {slot("detail").map((s) => <Section key={s.heading} s={s} />)}

      {/* 3. Who It's For */}
      {whoFor.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center text-navy"><Users size={22} /></span>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Who It's For</h2>
              </div>
              <p className="text-slate-600 text-base sm:text-lg mb-6">
                {service.who_for_intro || `Our ${service.title} service is ideal if you:`}
              </p>
              <ul className="space-y-3">
                {whoFor.map((w) => (
                  <li key={w} className="flex items-start gap-3">
                    <ArrowRight className="text-orange shrink-0 mt-1" size={18} />
                    <span className="text-[15px] text-navy leading-relaxed">{w}</span>
                  </li>
                ))}
              </ul>
              {service.who_for_footer && (
                <p className="text-slate-600 text-base sm:text-lg mt-6 leading-relaxed">{service.who_for_footer}</p>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {slot("compare").map((s) => <Section key={s.heading} s={s} />)}

      {/* Client evidence — one client's experience, attributed and caveated. */}
      {service.evidence && (
        <section className="py-16 md:py-20 bg-white border-t border-slate-100" data-testid="service-evidence">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">{service.evidence.heading}</h2>
              {(service.evidence.body || []).map((p, i) => (
                <p key={i} className="text-slate-600 text-base sm:text-lg leading-relaxed mb-4">{p}</p>
              ))}
              {service.evidence.quote && (
                <blockquote className="border-l-4 border-orange pl-5 my-6 text-navy text-lg italic leading-relaxed">
                  “{service.evidence.quote}”
                </blockquote>
              )}
              {service.evidence.link && (
                <Link to={service.evidence.link.to} data-testid="service-evidence-link"
                      className="inline-flex items-center gap-2 text-orange font-bold text-sm hover:gap-3 transition-all">
                  {service.evidence.link.label} <ArrowRight size={16} />
                </Link>
              )}
              {service.evidence.caveat && (
                <p className="text-xs text-slate-400 mt-4">{service.evidence.caveat}</p>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* 4. Why Choose CASS */}
      {service.why_choose && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange"><Award size={22} /></span>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Why Choose CASS?</h2>
              </div>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed whitespace-pre-line">{stripLeadingHeading(service.why_choose)}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* 5. FAQs */}
      {faqs.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center text-navy"><HelpCircle size={22} /></span>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Frequently Asked Questions</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3" data-testid="service-faqs">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-100 px-5">
                    <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed whitespace-pre-line">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}

      {/* 5b. Related guidance — the specific answers Paul named for this page,
          ahead of the automatic list below. Any he asked for that does not exist
          yet is recorded in stage1_service_pages.MISSING_GUIDANCE, not faked. */}
      {(service.related_guidance || []).length > 0 && (
        <section className="py-12 md:py-16 bg-white border-t border-slate-100" data-testid="service-related-guidance">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">Related guidance</h2>
              <ul className="space-y-2.5">
                {service.related_guidance.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="inline-flex items-start gap-2 text-navy hover:text-orange font-semibold text-[15px] transition-colors">
                      <ArrowRight className="text-orange shrink-0 mt-1" size={16} /> {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      {/* 5c. The answer library — one canonical version of every answer, linked
          rather than copied. */}
      <AskCassieRelated
        serviceUrl={`/services/${slug}`}
        category={subjectForService(slug)}
        topic={`${service.title} ${service.short || ""}`}
        heading={`More on ${service.title.toLowerCase()}, answered`}
      />

      {/* Related success stories */}
      {(() => {
        const relStories = (stories || []).filter((c) => (c.service_slugs || []).includes(service.slug)).slice(0, 3);
        if (relStories.length === 0) return null;
        return (
          <section className="py-16 md:py-20 bg-white" data-testid="service-success-stories">
            <div className="max-w-6xl mx-auto px-6">
              <div className="mb-8">
                <span className="text-xs font-bold uppercase tracking-widest text-orange">Proof, not promises</span>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-2">See how we've helped businesses like yours</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {relStories.map((c) => (
                  <Link key={c.id} to={`/success-stories/${c.slug}`} data-testid={`service-story-${c.slug}`} className="group bg-slate-50 rounded-2xl overflow-hidden border border-navy/5 hover:shadow-lg hover:-translate-y-1 transition-all">
                    <div className="aspect-[16/10] overflow-hidden bg-white">{c.image && <img loading="lazy" src={mediaUrl(c.image, 500)} alt={c.image_alt || c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                    <div className="p-5">
                      {c.industry && <span className="text-xs font-bold uppercase tracking-wide text-orange">{c.industry}</span>}
                      <h3 className="font-display font-bold text-navy leading-snug mt-1 group-hover:text-orange transition-colors line-clamp-2">{c.title}</h3>
                      {c.summary && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{c.summary}</p>}
                      <span className="text-sm font-bold text-orange inline-flex items-center gap-1 mt-3">Read their story <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* 6. Let's Talk About Your Business (static CTA) */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl uppercase leading-tight">{service.cta?.heading || CTA_HEADING}</h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl mx-auto whitespace-pre-line">{service.cta?.body || CTA_BODY}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {service.cta ? (
                <>
                  <BookCallButton testId="cta-discovery-call" label="Request a Discovery Call"
                                  className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" />
                  {packageLive && <Link to="/pricing" data-testid="cta-build-package" className="inline-flex items-center gap-2 border border-white/30 hover:border-orange hover:text-orange text-white font-bold px-8 py-4 rounded-full transition-colors">Build Your Indicative Price</Link>}
                </>
              ) : (
                <>
                  <Link to={`/contact?service=${encodeURIComponent(service.title)}`} data-testid="cta-lets-talk" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={17} /></Link>
                  {packageLive && <Link to="/pricing" data-testid="cta-build-package" className="inline-flex items-center gap-2 border border-white/30 hover:border-orange hover:text-orange text-white font-bold px-8 py-4 rounded-full transition-colors">What would CASS cost?</Link>}
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <GoogleReviewsSection testId="service-reviews" offset={(service.title || "").length} dark />

      {/* Related services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-2xl text-navy mb-8">Related services</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((s) => {
              const RIcon = getIcon(s.icon);
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-navy/5 flex items-center justify-center text-navy group-hover:bg-orange group-hover:text-white transition-colors mb-4"><RIcon size={22} /></div>
                  <h3 className="font-display font-bold text-navy mb-1">{s.title}</h3>
                  <span className="text-sm font-bold text-orange inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">Learn more <ArrowRight size={14} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
